import React, { useState, useMemo } from 'react';
import { Input, Select, Button, Space, Card, Typography, Checkbox, Divider, Alert, Tag, Collapse } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { addNewUser } from '../apiCalls';
import { getStoredUser } from '../utils/safeLocalStorage';
import * as Sentry from "@sentry/react";

// Role permissions mapping
const ROLE_PERMISSIONS = {
    'super-admin': [
        'create:users', 'read:users', 'update:users', 'delete:users',
        'create:calculations', 'read:calculations', 'update:calculations', 'delete:calculations',
        'create:properties', 'read:properties', 'update:properties', 'delete:properties',
        'manage:settings', 'manage:emails', 'manage:permissions', 'view:audit-logs',
        'quote.read', 'quote.write', 'quote.approve',
        'contract.create', 'contract.approve',
        'finance.read', 'finance.approve',
        'audit.read', 'integration.manage', 'procurement.read', 'procurement.write', 'procurement.approve',
    ],
    'tenant_admin': [
        'create:users', 'read:users', 'update:users',
        'read:calculations', 'update:calculations',
        'read:properties', 'update:properties',
        'view:audit-logs',
        'quote.read', 'quote.write', 'quote.approve',
        'contract.create', 'contract.approve',
        'finance.read', 'finance.approve',
        'audit.read', 'integration.manage', 'procurement.read', 'procurement.write', 'procurement.approve',
    ],
    'admin': [
        'create:users', 'read:users', 'update:users', 'delete:users',
        'create:calculations', 'read:calculations', 'update:calculations', 'delete:calculations',
        'read:properties', 'update:properties', 'view:audit-logs',
        'quote.read', 'quote.write', 'quote.approve',
        'finance.read', 'audit.read', 'integration.manage', 'procurement.read', 'procurement.write',
    ],
    'editor': [
        'read:users', 'create:calculations', 'read:calculations', 'update:calculations',
        'read:properties', 'quote.read', 'quote.write',
    ],
    'installer': [
        'read:properties', 'read:calculations', 'quote.read', 'procurement.read',
    ],
    'viewer': [
        'read:users', 'read:calculations', 'read:properties',
    ],
};

const getInitialState = (userRole, currentProperty) => ({
    firstName: '',
    lastName: '',
    email: '',
    property: userRole === 'superAdmin' || userRole === 'super-admin' || userRole === 'tenant_admin' || userRole === 'tenant-admin' ? '' : currentProperty,
    role: '',
    customPermissions: []
});

const getSchema = (isSuperAdmin, t) => Yup.object().shape({
    firstName: Yup.string()
        .min(2, t('users.validation.tooShort'))
        .max(50, t('users.validation.tooLong'))
        .required(t('users.validation.required')),
    lastName: Yup.string()
        .min(2, t('users.validation.tooShort'))
        .max(50, t('users.validation.tooLong'))
        .required(t('users.validation.required')),
    email: Yup.string()
        .email(t('users.validation.invalidEmail'))
        .required(t('users.validation.required')),
    property: isSuperAdmin
        ? Yup.string().required(t('users.validation.required'))
        : Yup.string(),
    role: Yup.string().required(t('users.validation.required')),
    customPermissions: Yup.array()
});

const CreateUser = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [showCustomPermissions, setShowCustomPermissions] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const pageLayoutProps = usePageLayout();
    const user = getStoredUser();

    const isSuperAdmin = user && ['superAdmin', 'super-admin'].includes(String(user.role || '').trim());
    const isAdmin = user && ['admin', 'Admin'].includes(String(user.role || '').trim());

    const userTypes = ['superAdmin', 'super-admin', 'tenant_admin', 'tenant-admin'];

    const getRoleOptions = () => {
        if (isSuperAdmin) {
            // Super admin can create any role
            return [
                { value: '', label: t('users.selectRole') },
                { value: 'super-admin', label: 'Super Admin' },
                { value: 'tenant_admin', label: 'Tenant Admin' },
                { value: 'admin', label: t('users.roleAdmin') },
                { value: 'editor', label: 'Editor' },
                { value: 'installer', label: t('users.roleUser') },
                { value: 'viewer', label: 'Viewer' }
            ];
        }
        if (isAdmin) {
            // Admin can create lesser roles only
            return [
                { value: '', label: t('users.selectRole') },
                { value: 'editor', label: 'Editor' },
                { value: 'installer', label: t('users.roleUser') },
                { value: 'viewer', label: 'Viewer' }
            ];
        }
        return [
            { value: '', label: t('users.selectRole') },
            { value: 'installer', label: t('users.roleUser') }
        ];
    };

    const getPropertyOptions = () => {
        if (user && userTypes.includes(String(user.role || '').trim())) {
            return pageLayoutProps.propList.map(prop => ({
                value: prop.propertyID,
                label: prop.companyName
            }));
        }
        return [];
    };

    const getAvailablePermissions = (selectedRole) => {
        return ROLE_PERMISSIONS[selectedRole] || [];
    };

    const canCustomizePermissions = () => {
        return isSuperAdmin || isAdmin;
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // Prepare user data with capabilities if custom permissions are selected
            const userData = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                property: values.property,
                role: values.role,
            };

            // Add custom permissions/capabilities if provided
            if (canCustomizePermissions() && values.customPermissions && values.customPermissions.length > 0) {
                userData.capabilities = values.customPermissions;
            }

            const response = await addNewUser(userData);

            if (response.status === 409) {
                setLoading(false);
                enqueueSnackbar(t('users.error.exists'), { variant: 'info' });
            } else if (response.status === 400) {
                setLoading(false);
                enqueueSnackbar(t('users.error.creation'), { variant: 'error' });
            } else if (response.status === 201) {
                setLoading(false);
                enqueueSnackbar(t('users.success.created'), { variant: 'success' });
                navigate('/');
            } else {
                setLoading(false);
                enqueueSnackbar(t('users.error.creation'), { variant: 'error' });
            }
        } catch (err) {
            setLoading(false);
            if (err.response?.status === 409) {
                enqueueSnackbar(t('users.error.exists'), { variant: 'info' });
            } else if (err.response?.status === 400) {
                enqueueSnackbar(t('users.error.creation'), { variant: 'error' });
            } else {
                const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || t('users.error.generic');
                enqueueSnackbar(errorMessage, { variant: 'error' });
            }
            Sentry.captureException(err);
        }
    };

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px' }}>
                <Card>
                    <Typography.Title level={2} style={{ marginBottom: 8 }}>
                        {t('users.createTitle')}
                    </Typography.Title>
                    <Typography.Text type="secondary" style={{ marginBottom: 32, display: 'block' }}>
                        {isSuperAdmin ? 'Create users with any role and customize permissions' :
                            isAdmin ? 'Create users with limited roles (Installer, Editor, Viewer)' :
                                'Create installer users for your property'}
                    </Typography.Text>

                    {!isSuperAdmin && !isAdmin && (
                        <Alert
                            message="Insufficient Permissions"
                            description="You don't have permission to create new users. Only Super Admin and Admin users can create users."
                            type="error"
                            showIcon
                            style={{ marginBottom: 32 }}
                        />
                    )}

                    <Formik
                        initialValues={getInitialState(user?.role, pageLayoutProps.property)}
                        validationSchema={getSchema(isSuperAdmin, t)}
                        onSubmit={handleSubmit}
                        enableReinitialize={true}
                    >
                        {({ handleChange, handleSubmit: formikHandleSubmit, values, errors, touched, setFieldValue, isSubmitting }) => {
                            const errorList = Object.keys(errors)
                                .filter(key => touched[key] && errors[key])
                                .map(key => ({
                                    field: key,
                                    message: errors[key]
                                }));

                            const availablePermissions = getAvailablePermissions(values.role);

                            return (
                                <form onSubmit={formikHandleSubmit} noValidate>
                                    {/* Error Summary */}
                                    {errorList.length > 0 && (
                                        <div
                                            role="alert"
                                            aria-live="polite"
                                            style={{
                                                backgroundColor: '#fff2f0',
                                                border: '1px solid #ffccc7',
                                                borderRadius: '4px',
                                                padding: '12px 16px',
                                                marginBottom: '24px'
                                            }}
                                        >
                                            <div style={{ color: '#d9534f', fontWeight: 600, marginBottom: '8px' }}>
                                                {t('users.errorsTitle')}
                                            </div>
                                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                {errorList.map((error, idx) => (
                                                    <li key={idx} style={{ color: '#d9534f', marginBottom: '4px' }}>
                                                        {error.message}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Basic Information Section */}
                                    <Typography.Title level={4} style={{ marginTop: 24, marginBottom: 16 }}>
                                        Basic Information
                                    </Typography.Title>

                                    {/* First Name */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label htmlFor="firstName" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                            {t('users.firstName')} <span style={{ color: '#d9534f' }}>*</span>
                                        </label>
                                        <Input
                                            id="firstName"
                                            type="text"
                                            name="firstName"
                                            placeholder={t('users.firstNamePlaceholder')}
                                            onChange={handleChange}
                                            value={values.firstName}
                                            maxLength={50}
                                            status={touched.firstName && errors.firstName ? 'error' : ''}
                                            aria-required="true"
                                            aria-invalid={touched.firstName && !!errors.firstName}
                                            aria-describedby={touched.firstName && errors.firstName ? 'firstName-error' : undefined}
                                        />
                                        {touched.firstName && errors.firstName && (
                                            <div id="firstName-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                                                {errors.firstName}
                                            </div>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label htmlFor="lastName" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                            {t('users.lastName')} <span style={{ color: '#d9534f' }}>*</span>
                                        </label>
                                        <Input
                                            id="lastName"
                                            type="text"
                                            name="lastName"
                                            placeholder={t('users.lastNamePlaceholder')}
                                            onChange={handleChange}
                                            value={values.lastName}
                                            maxLength={50}
                                            status={touched.lastName && errors.lastName ? 'error' : ''}
                                            aria-required="true"
                                            aria-invalid={touched.lastName && !!errors.lastName}
                                            aria-describedby={touched.lastName && errors.lastName ? 'lastName-error' : undefined}
                                        />
                                        {touched.lastName && errors.lastName && (
                                            <div id="lastName-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                                                {errors.lastName}
                                            </div>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div style={{ marginBottom: 24 }}>
                                        <label htmlFor="email" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                            {t('users.email')} <span style={{ color: '#d9534f' }}>*</span>
                                        </label>
                                        <Input
                                            id="email"
                                            type="email"
                                            name="email"
                                            placeholder={t('users.emailPlaceholder')}
                                            onChange={handleChange}
                                            value={values.email}
                                            maxLength={254}
                                            status={touched.email && errors.email ? 'error' : ''}
                                            aria-required="true"
                                            aria-invalid={touched.email && !!errors.email}
                                            aria-describedby={touched.email && errors.email ? 'email-error' : undefined}
                                        />
                                        {touched.email && errors.email && (
                                            <div id="email-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                                                {errors.email}
                                            </div>
                                        )}
                                    </div>

                                    <Divider />

                                    {/* Access Control Section */}
                                    <Typography.Title level={4} style={{ marginTop: 24, marginBottom: 16 }}>
                                        Access & Permissions
                                    </Typography.Title>

                                    {/* Property */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label htmlFor="property" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                            {t('users.property')}
                                            {(user && userTypes.includes(user.role)) && <span style={{ color: '#d9534f' }}>*</span>}
                                        </label>
                                        {(user && userTypes.includes(user.role)) ? (
                                            <>
                                                <Select
                                                    id="property"
                                                    name="property"
                                                    placeholder={t('users.selectProperty')}
                                                    onChange={(value) => setFieldValue('property', value)}
                                                    value={values.property || undefined}
                                                    options={getPropertyOptions()}
                                                    style={{ width: '100%' }}
                                                    status={touched.property && errors.property ? 'error' : ''}
                                                    aria-required="true"
                                                    aria-invalid={touched.property && !!errors.property}
                                                    aria-describedby={touched.property && errors.property ? 'property-error' : undefined}
                                                />
                                            </>
                                        ) : (
                                            <Input
                                                id="property"
                                                type="text"
                                                disabled
                                                value={pageLayoutProps.propList.find(p => p.propertyID === values.property)?.companyName || 'N/A'}
                                                aria-label="Current property (auto-assigned)"
                                            />
                                        )}
                                        {touched.property && errors.property && (
                                            <div id="property-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                                                {errors.property}
                                            </div>
                                        )}
                                    </div>

                                    {/* Role */}
                                    <div style={{ marginBottom: 24 }}>
                                        <label htmlFor="role" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                            {t('users.role')} <span style={{ color: '#d9534f' }}>*</span>
                                        </label>
                                        <Select
                                            id="role"
                                            name="role"
                                            placeholder={t('users.selectRole')}
                                            onChange={(value) => {
                                                setFieldValue('role', value);
                                                setFieldValue('customPermissions', []);
                                            }}
                                            value={values.role || undefined}
                                            options={getRoleOptions()}
                                            style={{ width: '100%' }}
                                            status={touched.role && errors.role ? 'error' : ''}
                                            aria-required="true"
                                            aria-invalid={touched.role && !!errors.role}
                                            aria-describedby={touched.role && errors.role ? 'role-error' : undefined}
                                        />
                                        {touched.role && errors.role && (
                                            <div id="role-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                                                {errors.role}
                                            </div>
                                        )}
                                        {values.role && availablePermissions.length > 0 && (
                                            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
                                                This role includes {availablePermissions.length} default permissions
                                            </Typography.Text>
                                        )}
                                    </div>

                                    {/* Custom Permissions Section - Only for Super Admin and Admin */}
                                    {canCustomizePermissions() && values.role && availablePermissions.length > 0 && (
                                        <>
                                            <Divider />

                                            <div style={{ marginBottom: 24 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                                    <Checkbox
                                                        checked={showCustomPermissions}
                                                        onChange={(e) => setShowCustomPermissions(e.target.checked)}
                                                    >
                                                        <span style={{ fontWeight: 500 }}>Customize Permissions</span>
                                                    </Checkbox>
                                                </div>

                                                {showCustomPermissions && (
                                                    <div style={{
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: '4px',
                                                        padding: '16px',
                                                        backgroundColor: '#fafafa'
                                                    }}>
                                                        <Typography.Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
                                                            Select additional permissions beyond the role defaults. Permissions are grouped by category.
                                                        </Typography.Text>

                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                                            {availablePermissions.map((permission) => (
                                                                <div key={permission}>
                                                                    <Checkbox
                                                                        checked={values.customPermissions?.includes(permission)}
                                                                        onChange={(e) => {
                                                                            const permissions = values.customPermissions || [];
                                                                            if (e.target.checked) {
                                                                                setFieldValue('customPermissions', [...permissions, permission]);
                                                                            } else {
                                                                                setFieldValue('customPermissions', permissions.filter(p => p !== permission));
                                                                            }
                                                                        }}
                                                                    >
                                                                        <span style={{ fontSize: '12px' }}>{permission}</span>
                                                                    </Checkbox>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {values.customPermissions && values.customPermissions.length > 0 && (
                                                            <div style={{ marginTop: 16 }}>
                                                                <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                                                                    Selected: {values.customPermissions.length} additional permissions
                                                                </Typography.Text>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                    {values.customPermissions.map((perm) => (
                                                                        <Tag key={perm} color="blue" closable onClose={() => {
                                                                            setFieldValue('customPermissions', values.customPermissions.filter(p => p !== perm));
                                                                        }}>
                                                                            {perm}
                                                                        </Tag>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <Divider />

                                    {/* Buttons - With Extra Spacing */}
                                    <div style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-start', gap: 12 }}>
                                        <Button
                                            onClick={() => navigate('/')}
                                            disabled={loading || isSubmitting}
                                        >
                                            {t('common.cancel')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            disabled={loading || isSubmitting || (!isSuperAdmin && !isAdmin)}
                                            loading={loading}
                                        >
                                            {loading ? t('users.creating') : t('users.createButton')}
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </Card>
            </div>
        </PageLayout>
    );
};

export default CreateUser;
